import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer, View, EventProps } from "react-big-calendar";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useTranslation } from "react-i18next";
import { IconWarningAlt, IconArrowLeft, IconArrowRight } from "@veneer/core";
import { IGetAppointmentOccurrenceByUser } from "../../models/dto/agendaApi/IGetAppointmentsByUser";
import {
  ICalendarEvent,
  DeleteOccurrenceMode,
  mapAppointmentsToCalendarEvents,
  isPastOccurrence,
} from "./agendaCalendarUtils";
import AgendaEventCard from "./AgendaEventCard";
import RecurrenceActionModal from "./RecurrenceActionModal";
import "./agendaCalendar.css";

const localizer = momentLocalizer(moment);

const AgendaToolbar = ({ label, onNavigate, view, onView, views }: any) => {
  const [t] = useTranslation("global");
  const viewLabels: Record<string, string> = {
    week:   t("header.calendarWeek"),
    day:    t("header.calendarDay"),
    agenda: t("header.calendarAgenda"),
  };
  const viewList: View[] = Array.isArray(views)
    ? views
    : (Object.keys(views) as View[]);
  return (
    <div className="agendaToolbar">
      <div className="agendaToolbarNav">
        <button
          type="button"
          className="agendaToolbarNavBtn"
          onClick={() => onNavigate("PREV")}
          aria-label="Previous"
        >
          <IconArrowLeft size={18} />
        </button>
        <button
          type="button"
          className="agendaToolbarTodayBtn"
          onClick={() => onNavigate("TODAY")}
        >
          {t("header.calendarToday")}
        </button>
        <button
          type="button"
          className="agendaToolbarNavBtn"
          onClick={() => onNavigate("NEXT")}
          aria-label="Next"
        >
          <IconArrowRight size={18} />
        </button>
      </div>
      <span className="agendaToolbarLabel">{label}</span>
      <div className="agendaToolbarViews">
        {viewList.map((v) => (
          <button
            key={v}
            type="button"
            className={`agendaToolbarViewBtn${
              view === v ? " agendaToolbarViewBtnActive" : ""
            }`}
            onClick={() => onView(v)}
          >
            {viewLabels[v] ?? (v.charAt(0).toUpperCase() + v.slice(1))}
          </button>
        ))}
      </div>
    </div>
  );
};

interface AgendaCalendarProps {
  appointments: IGetAppointmentOccurrenceByUser[];
  onEditSeries: (event: ICalendarEvent) => void;
  onEditOccurrences: (event: ICalendarEvent) => void;
  onDeleteSeries: (event: ICalendarEvent) => void;
  onDeleteOccurrence: (event: ICalendarEvent, mode: DeleteOccurrenceMode) => void;
  onPastOccurrenceRestricted: () => void;
  onCheckedInOccurrenceRestricted?: () => void;
}

const AgendaCalendar: React.FC<AgendaCalendarProps> = ({
  appointments,
  onEditSeries,
  onEditOccurrences,
  onDeleteSeries,
  onDeleteOccurrence,
  onPastOccurrenceRestricted,
  onCheckedInOccurrenceRestricted,
}) => {
  const [t, i18n] = useTranslation("global");
  const [view, setView] = useState<View>("week");

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  const calendarMessages = useMemo(() => ({
    date:           t("header.calendarDate"),
    time:           t("header.calendarTime"),
    event:          t("header.calendarEvent"),
    noEventsInRange: t("header.calendarNoEvents"),
  }), [t]);
  const [recurrenceEvent, setRecurrenceEvent] = useState<ICalendarEvent | null>(null);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);

  const events = useMemo(
    () => mapAppointmentsToCalendarEvents(appointments),
    [appointments]
  );

  const handleRecurrenceClick = useCallback((event: ICalendarEvent) => {
    if (isPastOccurrence(event.start, event.end)) {
      onPastOccurrenceRestricted();
      return;
    }

    const occ = event.occurrenceID !== null
      ? event.resource.occurrences?.find((o) => o.appointmentOccurrenceID === event.occurrenceID)
      : undefined;
    if (occ?.isCheckIn === true) {
      const n = new Date();
      const today = new Date(n.getFullYear(), n.getMonth(), n.getDate());
      const eventDay = new Date(event.start.getFullYear(), event.start.getMonth(), event.start.getDate());
      if (eventDay.getTime() === today.getTime()) {
        if (onCheckedInOccurrenceRestricted) {
          onCheckedInOccurrenceRestricted();
        } else {
          onPastOccurrenceRestricted();
        }
        return;
      }
    }

    setRecurrenceEvent(event);
    setShowRecurrenceModal(true);
  }, [onPastOccurrenceRestricted, onCheckedInOccurrenceRestricted]);

  const closeRecurrenceModal = useCallback(() => {
    setShowRecurrenceModal(false);
    setRecurrenceEvent(null);
  }, []);

  const eventPropGetter = useCallback(
    () => ({ className: "agendaCalendarEvent" }),
    []
  );

  const EventComponent = useCallback(
    ({ event }: EventProps<ICalendarEvent>) => (
      <AgendaEventCard
        event={event}
        onRecurrenceClick={handleRecurrenceClick}
        isAgendaView={view === "agenda"}
      />
    ),
    [handleRecurrenceClick, view]
  );

  const EventWrapper = useCallback(({ children }: any) => {
    const child = React.Children.only(children) as React.ReactElement<any>;
    return React.cloneElement(child, {
      style: {
        ...child.props.style,
        height: "auto",
        minHeight: 0,
      },
    });
  }, []);

  const components = useMemo(
    () => ({ event: EventComponent, eventWrapper: EventWrapper, toolbar: AgendaToolbar }),
    [EventComponent, EventWrapper]
  );

  if (appointments.length === 0) {
    return (
      <div className="agendaEmptyState">
        <div className="customNoItems">
          <div className="DivIconCustomNoItems">
            <IconWarningAlt className="IconCustomNoItemsOutTable" />
          </div>
          {t("header.noItemsFound")}
        </div>
      </div>
    );
  }

  return (
    <div className="agendaCalendarWrapper">
      <Calendar<ICalendarEvent>
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        titleAccessor="title"
        view={view}
        onView={setView}
        views={["week", "day", "agenda"]}
        eventPropGetter={eventPropGetter}
        components={components}
        culture={i18n.language}
        messages={calendarMessages}
        style={{ height: 600 }}
        defaultDate={new Date()}
      />
      <RecurrenceActionModal
        show={showRecurrenceModal}
        event={recurrenceEvent}
        canEditOccurrences={true}
        canDeleteOccurrence={
          recurrenceEvent ? recurrenceEvent.occurrenceID !== null : false
        }
        onClose={closeRecurrenceModal}
        onEditSeries={(evt) => {
          onEditSeries(evt);
          closeRecurrenceModal();
        }}
        onEditOccurrences={(evt) => {
          onEditOccurrences(evt);
          closeRecurrenceModal();
        }}
        onDeleteSeries={(evt) => {
          onDeleteSeries(evt);
          closeRecurrenceModal();
        }}
        onDeleteOccurrence={(evt, mode) => {
          onDeleteOccurrence(evt, mode);
        }}
      />
    </div>
  );
};

export default AgendaCalendar;
